import React, { useState } from "react";
import { PaziaLoader } from "components/atoms";
import { DeleteButton } from "../../../../components/atoms/Buttons/Icons/DeleteButton";
import { useCourseDocumentExtraPages, useDeleteExtraPage, useReorderExtraPages } from "api/hooks";
import { CourseDocumentExtraPageType } from "api/courseDocumentExtraPagesApi";

interface ExtraPagesListProps {
  courseId: number;
  courseDocumentId: number;
  onListChange?: () => void;
}

export const ExtraPagesList: React.FC<ExtraPagesListProps> = ({ courseId, courseDocumentId, onListChange }) => {
  const { loading, data: extraPages, refresh } = useCourseDocumentExtraPages(courseId, courseDocumentId);
  const deleteMutation = useDeleteExtraPage(courseId, courseDocumentId);
  const reorderMutation = useReorderExtraPages(courseId, courseDocumentId);
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);

  const handleDelete = async (pageId: number) => {
    try {
      await deleteMutation.mutateAsync(pageId);
      setDeleteConfirm(null);
      if (onListChange) {
        onListChange();
      }
    } catch (error) {
      console.error("Delete failed:", error);
    }
  };

  const getFileTypeLabel = (fileType: CourseDocumentExtraPageType): string => {
    switch (fileType) {
      case CourseDocumentExtraPageType.PDF:
        return "PDF";
      case CourseDocumentExtraPageType.DOCX:
        return "DOCX";
      default:
        return "Nieznany";
    }
  };

  const getFileTypeIcon = (fileType: CourseDocumentExtraPageType): string => {
    switch (fileType) {
      case CourseDocumentExtraPageType.PDF:
        return "📄";
      case CourseDocumentExtraPageType.DOCX:
        return "📝";
      default:
        return "📁";
    }
  };

  const moveUp = (index: number) => {
    if (index === 0) return;
    const newOrders: Record<string, number> = {};
    const currentPage = extraPages[index];
    const previousPage = extraPages[index - 1];

    newOrders[currentPage.id.toString()] = previousPage.order;
    newOrders[previousPage.id.toString()] = currentPage.order;

    reorderMutation.mutate(newOrders);
  };

  const moveDown = (index: number) => {
    if (index === extraPages.length - 1) return;
    const newOrders: Record<string, number> = {};
    const currentPage = extraPages[index];
    const nextPage = extraPages[index + 1];

    newOrders[currentPage.id.toString()] = nextPage.order;
    newOrders[nextPage.id.toString()] = currentPage.order;

    reorderMutation.mutate(newOrders);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-4">
        <PaziaLoader />
        <span className="ml-2">Ładowanie dodatkowych stron...</span>
      </div>
    );
  }

  if (extraPages.length === 0) {
    return (
      <div className="p-4 text-center text-sm text-gray-500 sm:text-base">
        Brak dodatkowych stron. Dodaj pierwszą używając formularza powyżej.
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <h4 className="text-base font-semibold sm:text-lg">Dodatkowe strony ({extraPages.length})</h4>

      {extraPages.map((page, index) => (
        <div
          key={page.id}
          className="flex flex-col justify-between space-y-3 rounded-lg border bg-gray-50 p-3 sm:flex-row sm:items-center sm:space-y-0"
        >
          <div className="flex min-w-0 flex-1 items-center space-x-3">
            <span className="flex-shrink-0 text-xl sm:text-2xl">{getFileTypeIcon(page.fileType)}</span>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-medium sm:text-base">{page.fileName}</div>
              <div className="text-xs text-gray-500 sm:text-sm">
                {getFileTypeLabel(page.fileType)} • Kolejność: {page.order}
              </div>
            </div>
          </div>

          <div className="flex flex-shrink-0 items-center justify-end space-x-2">
            {/* Move up button */}
            <button
              onClick={() => moveUp(index)}
              disabled={index === 0 || reorderMutation.isPending}
              className="p-1.5 text-gray-400 hover:text-gray-600 disabled:cursor-not-allowed disabled:opacity-50 sm:p-1"
              title="Przenieś wyżej"
            >
              <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </button>

            {/* Move down button */}
            <button
              onClick={() => moveDown(index)}
              disabled={index === extraPages.length - 1 || reorderMutation.isPending}
              className="p-1.5 text-gray-400 hover:text-gray-600 disabled:cursor-not-allowed disabled:opacity-50 sm:p-1"
              title="Przenieś niżej"
            >
              <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </button>

            {deleteConfirm === page.id ? (
              <div className="flex items-center space-x-1 sm:space-x-2">
                <button
                  onClick={() => handleDelete(page.id)}
                  disabled={deleteMutation.isPending}
                  className="rounded bg-red-600 px-2 py-1 text-xs text-white hover:bg-red-700 disabled:opacity-50"
                >
                  Potwierdź
                </button>
                <button
                  onClick={() => setDeleteConfirm(null)}
                  className="rounded bg-gray-400 px-2 py-1 text-xs text-white hover:bg-gray-500"
                >
                  Anuluj
                </button>
              </div>
            ) : (
              <DeleteButton
                onClick={() => setDeleteConfirm(page.id)}
                // disabled={deleteMutation.isPending}
                // title="Usuń stronę"
              />
            )}
          </div>
        </div>
      ))}

      {(deleteMutation.isError || reorderMutation.isError) && (
        <div className="px-2 text-xs text-red-600 sm:text-sm">
          Wystąpił błąd podczas wykonywania operacji. Spróbuj ponownie.
        </div>
      )}
    </div>
  );
};
